import React from 'react';
import { useTranslation } from 'react-i18next';
import UserAvatar from './UserAvatar';
import { format, isToday, isYesterday } from 'date-fns';

const ConversationItem = ({ 
  conversation, 
  onClick, 
  isActive = false 
}) => {
  const { t } = useTranslation();

  // Get the other participant info from conversation fields
  const otherParticipant = {
    id: conversation.other_user_id,
    username: conversation.other_username,
    display_name: conversation.other_display_name,
    avatar_url: conversation.other_avatar_url
  };
  const lastMessage = conversation.last_message_text || null;
  const unreadCount = conversation.unread_count || 0;
  const lastMessageAt = conversation.last_message_at;

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return t('common.yesterday', { defaultValue: 'Yesterday' });
    } else {
      const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      if (daysDiff < 7) {
        return format(date, 'EEEE'); // Day name (Monday, Tuesday, etc.)
      } else {
        return format(date, 'dd/MM/yyyy');
      }
    }
  };

  // Truncate message preview
  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return t('chat.noMessages', { defaultValue: 'No messages yet' });
    return message.length > maxLength 
      ? `${message.substring(0, maxLength)}...` 
      : message;
  };

  return (
    <div
      className={`relative flex items-center p-3 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-zinc-800/50 border-b border-gray-100 dark:border-zinc-700/50 group ${
        isActive 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700/50' 
          : ''
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* User Avatar */}
      <div className="relative flex-shrink-0 mr-3">
        <UserAvatar
          user={otherParticipant}
          size="w-12 h-12"
          textSize="text-sm"
          showBorder={false}
          clickable={false}
        />
        {/* Online status indicator (can be added later) */}
        {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-zinc-800 rounded-full"></div> */}
      </div>

      {/* Conversation Info */}
      <div className="flex-1 min-w-0">
        {/* Name and Timestamp Row */}
        <div className="flex items-center justify-between mb-1">
          <h3 className={`font-semibold text-sm truncate ${
            isActive 
              ? 'text-blue-700 dark:text-blue-300' 
              : 'text-white'
          } ${unreadCount > 0 ? 'font-bold' : ''}`}>
            {otherParticipant?.display_name || otherParticipant?.username || t('common.unknownUser', { defaultValue: 'Unknown User' })}
          </h3>
          <span className={`text-xs flex-shrink-0 ml-2 ${
            isActive 
              ? 'text-blue-600 dark:text-blue-400' 
              : 'text-gray-500 dark:text-gray-400'
          } ${unreadCount > 0 ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}>
            {formatTimestamp(lastMessageAt)}
          </span>
        </div>

        {/* Last Message and Unread Badge Row */}
        <div className="flex items-center justify-between">
          <p className={`text-sm truncate pr-2 ${
            unreadCount > 0 
              ? 'text-white font-medium' 
              : 'text-gray-600 dark:text-gray-400'
          }`}>
            {truncateMessage(lastMessage)}
          </p>
          
          {/* Unread Count Badge */}
          {unreadCount > 0 && (
            <div className="flex-shrink-0 bg-blue-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </div>
      </div>

      {/* Hover Indicator */}
      <div className="absolute inset-y-0 left-0 w-1 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
    </div>
  );
};

export default ConversationItem;